import React, { Component } from 'react';
import _ from 'lodash';
import { Dropdown } from 'react-bootstrap';
import clsx from 'clsx';
import styles from 'components/ui/dropdown/BlazeDropdown/BlazeDropdown.module.css';
import BzmSearchInput from 'components/forms/BzmSearchInput/BzmSearchInput';
import NoContentPlaceholder from 'components/ui/noContentPlaceholder/NoContentPlaceholder';
import SimpleLoader from 'components/loaders/SimpleLoader';
import { buildRegExp } from 'utils/fileUtils';
import Button from 'react-bootstrap/Button';
import i18n from 'i18n';
import { FilterFooter } from 'types/rest/assetCatalogTypes';
import BlazeDropdownToggle from './BlazeDropdownToggle';

export interface BlazeDropdownItem {
  /** Mandatory, used as key. Must be unique. */
  id: string | number;

  /** Name string used in title */
  name: string;

  /** Custom html content, if set it will be placed in the body of the item. */
  htmlContent?: any;

  /** Custom html title, if set it will be placed in the selected item in the dropdown */
  htmlTitle?: any;

  /** Group which this item belongs to */
  group?: string;

  /** Any additional data */
  metaData?: any;
}

export interface BlazeDropdownProps {
  /** Groups  */
  items: BlazeDropdownItem[];

  /** On item click handler */
  onClick: (item: BlazeDropdownItem) => void;

  /** Ids of the selected items */
  selected: BlazeDropdownItem | null;

  disabled?: boolean;

  /** Hide / Show search bar with client side search */
  isSearchable?: boolean;

  /** Unique id */
  id?: string;

  /** Controls loading indicator */
  isLoading?: boolean;

  /** Placeholder shown if there are no items available */
  noItemsPlaceholder?: string | any;

  /** Placeholder in the search field */
  searchPlaceholder?: string;

  /** Minimum width of the dropdown menu */
  minDropdownMenuWidth?: number | string;

  /** Minimum height of the dropdown, if the content exceeds this number it will become scrollable. */
  maxDropdownHeight?: number;

  /** Shows two level hierarchies if set to true, items are grouped by "group" attribute. */
  showGroups?: boolean;

  /** Default option which can be used as custom value in the input. */
  defaultOption?: BlazeDropdownItem;

  /** If set to true, a default option is also available in the dropdown content as a first item. */
  isDefaultOptionSelectable?: boolean;

  /** Custom dropdown footer element. Can be any React component. */
  dropDownFooter?: any | FilterFooter;

  /** If it is true adds highlight style to the dropdown.*/
  isHighlighted?: boolean;

  /** if it is true, add the top loading icon to the button. */
  isLoadingButton?: boolean;

  /** Either show/hide title. */
  isTitleHidden?: boolean;

  /** flag to focus on dropdown field itself when rendered page */
  focus?: boolean;

  /** Dropdown toggle */
  toggleElement?: any;

  /** custom padding if needed */
  padding?: string;

  /** Custom class name added to every item in the dropdown*/
  dropDownItemClassName?: any;

  /** Width of the dropdown element. */
  dropdownWidth?: number | string;

  height?: number;
}

/**
 * Dropdown component with optional search field.
 * It can produce either a flat list of items
 * or simple hierarchy with custom grouping.
 */
class BlazeInputDropdown extends Component<BlazeDropdownProps> {
  searchInputRef: any = React.createRef();
  toggleRef: React.RefObject<any> = React.createRef();

  state = {
    searchKeyWord: undefined,
    displayedResults: 0,
  };

  filterChanged = (value: string) => {
    this.setState({ searchKeyWord: value });
  };

  createGroups = (items: BlazeDropdownItem[]) => {
    return (
      _.chain(items)

        // Group the elements of Array based on `group` property
        .groupBy('group')

        // `key` is group's name (harborId), `value` is the array of objects
        .map((value, key) => ({ groupName: key !== 'null' ? key : '-', children: value }))
        .value()
    );
  };

  /**
   * Filters items by search string.
   * @param items
   * @private
   */
  _filterItemsByKeyword = (items: BlazeDropdownItem[]) => {
    return items.filter((item: BlazeDropdownItem) => {
      if (this.state.searchKeyWord === undefined) {
        return true;
      }
      return _.includes(item.name.toLowerCase(), (('' + this.state.searchKeyWord) as string).toLowerCase());
    });
  };

  /**
   * Gets focus on the search by ref.
   */
  _handleAutoFocus = async () => {
    setTimeout(() => {
      if (this.searchInputRef.current !== null) {
        this.searchInputRef.current.focus();
      }
    }, 100);
  };

  isFilterFooterType = (dropDownFooter: any) => {
    return dropDownFooter !== undefined && dropDownFooter.hasOwnProperty('entity');
  };

  /**
   * Creates single item mark up.
   * @param dropDownItem
   * @param isGroupItem - indicates whether item is part of the group (needs indentation)
   * @param suffix - suffix for the menu item
   * @private
   */
  createDropdownItemDom = (dropDownItem: BlazeDropdownItem, isGroupItem: boolean, suffix?: string) => {
    let name = dropDownItem.name;
    name = buildRegExp(name, this.state.searchKeyWord);

    let dropDownItemContent;
    const isSelected = this.props.selected && this.props.selected.id === dropDownItem.id;
    const classNames = clsx(
      'dropdown-item',
      'dropdown-item-' + suffix ? suffix : _.uniqueId(),
      isSelected ? 'active' : '',
      isGroupItem ? 'dropdown-item-nested-1' : '',
    );

    // Do not return the whole object with custom html, it is too expensive
    const itemReference = {
      id: dropDownItem.id,
      name: dropDownItem.name,
      metaData: dropDownItem.metaData,
      group: dropDownItem.group,
    };

    if (dropDownItem.htmlContent) {
      // If custom html content is provided, render it
      dropDownItemContent = (
        <Dropdown.Item
          className={this.props.dropDownItemClassName ? this.props.dropDownItemClassName : classNames}
          data-title={dropDownItem.name}
          onClick={() => this.props.onClick(itemReference)}
          key={dropDownItem.id}
          title={!this.props.isTitleHidden ? dropDownItem.name : undefined}
          style={{ padding: this.props.padding }}
        >
          {dropDownItem.htmlContent}
        </Dropdown.Item>
      );
    } else {
      dropDownItemContent = (
        // Use name value as a inner content
        <Dropdown.Item
          data-title={dropDownItem.name}
          className={this.props.dropDownItemClassName ? this.props.dropDownItemClassName : classNames}
          onClick={() => this.props.onClick(itemReference)}
          key={dropDownItem.id}
          title={!this.props.isTitleHidden ? dropDownItem.name : ''}
          dangerouslySetInnerHTML={{ __html: name }}
        ></Dropdown.Item>
      );
    }

    return dropDownItemContent;
  };

  _createDropdownItemsDom = () => {
    let items: BlazeDropdownItem[] = this.props.items;

    if (this.props.isLoading === true) {
      return (
        <div className="text-center p-4">
          <SimpleLoader />
        </div>
      );
    }

    //Empty placeholder
    if (!this.props.items || this.props.items.length === 0) {
      return (
        <NoContentPlaceholder
          placeHolderContent={this.props.noItemsPlaceholder ? this.props.noItemsPlaceholder : 'No items available.'}
          height={this.props.height ? this.props.height : 120}
        />
      );
    }

    // Filter by search keyword
    items = this._filterItemsByKeyword(items);

    // If search is enabled but no results are found
    if (
      !this.isFilterFooterType(this.props.dropDownFooter) &&
      this.state.searchKeyWord !== undefined &&
      items.length === 0
    ) {
      return <div className="p-3 text-center">There are no results.</div>;
    }

    let dropdownItems: any;

    // If a group option is enabled, group content by "group" attribute and show as hierarchy
    if (this.props.showGroups === true) {
      const groupedItems = this.createGroups(items);

      // Create groups
      dropdownItems = groupedItems.map(item => {
        return (
          <div key={item.groupName} className="blaze-dropdown-group mb-2">
            <div className={'dropdown-item-text'}>
              <strong>{item.groupName}</strong>
            </div>
            {item.children.map((dropDownItem: BlazeDropdownItem) => this.createDropdownItemDom(dropDownItem, true))}
          </div>
        );
      });
    } else {
      // Show a flat list
      dropdownItems = items.map((item, index) => {
        const suffix = 'index-' + index.toString();
        return this.createDropdownItemDom(item, false, suffix);
      });
    }

    return (
      <>
        {/* Default option item */}
        {(this.state.searchKeyWord !== undefined || this.state.searchKeyWord !== '') &&
          this.props.isDefaultOptionSelectable &&
          this.props.defaultOption &&
          this.createDropdownItemDom(this.props.defaultOption, false, 'default-item')}
        {/* Default option item */}

        {/* Other items */}
        {dropdownItems}
        {/* Other items */}
      </>
    );
  };

  createDropDownFooterWhenCreationEnabled = (footer: FilterFooter) => {
    let creatableFooter: JSX.Element;
    // There are some data in the backend
    if (this.props.items.length > 0) {
      // There is no service matching the search, allow user to create it

      const filteredItems = this._filterItemsByKeyword(this.props.items);
      let fullMatch = false;
      if (
        filteredItems.length === 1 &&
        this.state.searchKeyWord !== undefined &&
        ((this.state.searchKeyWord as unknown) as string).toLowerCase().trim() ===
          filteredItems[0].name.toLocaleLowerCase()
      ) {
        fullMatch = true;
      }

      if (
        fullMatch === false &&
        this.state.searchKeyWord !== undefined &&
        this.state.searchKeyWord !== '' &&
        ((this.state.searchKeyWord as unknown) as string).toLowerCase().trim().length > 0
      ) {
        creatableFooter =
          this.props.dropDownFooter.style === 'Text' ? (
            <div className="text-left">
              <button
                type={'button'}
                onClick={() => {
                  footer.onClick((this.state.searchKeyWord as unknown) as string);
                  this.setState({ searchKeyWord: '' });
                }}
                className="btn btn-link font-italic"
              >
                {i18n.t('common:blazeDropdown.addEntityPlus', { entity: footer.entity })}{' '}
                <span className={'font-italic-weight-semi-bold'}>
                  {'"'}
                  {this.state.searchKeyWord}
                  {'"'}
                </span>
              </button>
            </div>
          ) : (
            <div className="text-center">
              <p>
                {footer.entity + ' '}
                <b className="text-break" title={this.state.searchKeyWord}>
                  {this.state.searchKeyWord}
                </b>{' '}
                {i18n.t('common:blazeDropdown.notFound')} <span>{i18n.t('common:blazeDropdown.doYouWantToAddIt')}</span>
              </p>
              <Button onClick={_.noop} className="qa-create-service">
                {i18n.t('common:blazeDropdown.addEntity', { entity: footer.entity })}
              </Button>
            </div>
          );
      } else {
        creatableFooter =
          this.props.dropDownFooter.style === 'Text' ? (
            <div className="text-left">
              <button type={'button'} onClick={() => footer.onClick('')} className="btn btn-link font-italic">
                {i18n.t('common:blazeDropdown.addEntityPlus', { entity: footer.entity })}
              </button>
            </div>
          ) : (
            <div className="text-center">
              <Button onClick={_.noop} className="qa-create-service">
                {i18n.t('common:blazeDropdown.addEntity', { entity: footer.entity })}
              </Button>
            </div>
          );
      }
    } else {
      creatableFooter =
        this.props.dropDownFooter.style === 'Text' ? (
          <div className="text-left">
            <button
              type={'button'}
              onClick={() =>
                footer.onClick(
                  this.state.searchKeyWord !== undefined ? ((this.state.searchKeyWord as unknown) as string) : '',
                )
              }
              className="btn btn-link font-italic"
            >
              {i18n.t('common:blazeDropdown.addEntityPlus', { entity: footer.entity })}
            </button>
          </div>
        ) : (
          <div className="text-center">
            <p> {i18n.t('common:blazeDropdown.noEntityAvailableCreateOne', { entity: footer.entity })} </p>
            <Button onClick={_.noop} className="qa-create-service" disabled={this.state.searchKeyWord === ''}>
              {i18n.t('common:blazeDropdown.addEntity', { entity: footer.entity })}
            </Button>
          </div>
        );
    }
    return creatableFooter;
  };

  render() {
    const search = (
      <BzmSearchInput
        autoFocus={true}
        placeholder={this.props.searchPlaceholder}
        value={this.state.searchKeyWord}
        onChange={this.filterChanged}
        disabled={_.size(this.props.items) === 0}
        searchRef={this.searchInputRef}
      />
    );
    const items = this._createDropdownItemsDom();
    const defaultOption = this.props.defaultOption ? this.props.defaultOption.name : 'Please Select';
    const selectedValue = this.props.selected
      ? this.props.selected.htmlTitle
        ? this.props.selected.htmlTitle
        : this.props.selected.name
      : defaultOption;
    const toggleStyle = this.props.isHighlighted ? { borderColor: '#f6c41a', backgroundColor: '#f6c41a' } : undefined;
    const dropdownStyle = this.props.dropdownWidth ? { width: this.props.dropdownWidth } : undefined;
    return (
      <Dropdown id={'dropdown-' + this.props.id} style={dropdownStyle}>
        <Dropdown.Toggle
          as={BlazeDropdownToggle}
          disabled={this.props.disabled}
          id={this.props.id ? this.props.id : _.uniqueId('dropdown-toggle-')}
          // todo - fix
          // handleAutoFocus={this._handleAutoFocus}
          // focus={this.props.focus}
          // isLoadingButton={isLoadingButton}
          style={toggleStyle}
        >
          {selectedValue}
        </Dropdown.Toggle>
        <Dropdown.Menu
          popperConfig={{ strategy: 'fixed' }}
          className={toggleStyle ? styles.dropdownMenuRight : styles.dropdownMenu}
          style={{
            minWidth: this.props.minDropdownMenuWidth ? this.props.minDropdownMenuWidth : undefined,
          }}
        >
          {/* Search Bar */}
          {this.props.isSearchable === true && <div>{search}</div>}
          {/* ./Search Bar */}
          {/* Items */}
          <div
            className="dropdown-menu-limited"
            style={{
              maxHeight: this.props.maxDropdownHeight ? this.props.maxDropdownHeight : 'auto',
            }}
          >
            {items}
          </div>
          {/* ./Items */}

          {/* Footer */}
          {this.props.dropDownFooter && (
            <div className={styles.dropdownFooter}>
              {this.isFilterFooterType(this.props.dropDownFooter)
                ? this.createDropDownFooterWhenCreationEnabled(this.props.dropDownFooter)
                : this.props.dropDownFooter}
            </div>
          )}

          {/* Footer */}
        </Dropdown.Menu>
      </Dropdown>
    );
  }
}

export default BlazeInputDropdown;
